import { Box, Checkbox, Loader, ScrollArea, Text } from '@mantine/core'
import { Link } from 'react-router-dom'
import { useLegalText } from '../../hooks/useLegalText'

const GUIDELINES_PATH = '/guidelines'
const SLOP_CONSENT_PATH = '/slop-feed'
const TERMS_PATH = '/terms'
const PRIVACY_PATH = '/privacy'

export type LegalConsentTextKey =
  | 'slop-feed-consent'
  | 'community-guidelines'
  | 'terms-of-service'
  | 'privacy-policy'

export interface LegalConsentCheckboxProps {
  legalTextKey: LegalConsentTextKey
  checked: boolean
  onChange: (checked: boolean) => void
  /** Optional link label override. */
  policyLinkLabel?: string
  policyHref?: string
}

export default function LegalConsentCheckbox ({
  legalTextKey,
  checked,
  onChange,
  policyLinkLabel,
  policyHref
}: LegalConsentCheckboxProps) {
  const { text, loading, error } = useLegalText(legalTextKey)

  const href = policyHref ?? (
    legalTextKey === 'slop-feed-consent'
      ? SLOP_CONSENT_PATH
      : legalTextKey === 'terms-of-service'
        ? TERMS_PATH
        : legalTextKey === 'privacy-policy'
          ? PRIVACY_PATH
          : GUIDELINES_PATH
  )
  const linkLabel = policyLinkLabel ?? (
    legalTextKey === 'slop-feed-consent'
      ? 'Slop Feed consent'
      : legalTextKey === 'terms-of-service'
        ? 'Terms of Service'
        : legalTextKey === 'privacy-policy'
          ? 'Privacy Policy'
          : 'Community guidelines'
  )

  if (loading) {
    return (
      <Box py="xs">
        <Loader size="sm" />
      </Box>
    )
  }

  if (error || !text) {
    return (
      <Text size="sm" color="red">
        Could not load consent terms. Try again later.
      </Text>
    )
  }

  return (
    <Box>
      <ScrollArea.Autosize mah={120} mb="sm" offsetScrollbars>
        <Text size="xs" sx={{ whiteSpace: 'pre-wrap', color: 'var(--gs-muted)' }}>
          {text.body}
        </Text>
      </ScrollArea.Autosize>
      <Checkbox
        checked={checked}
        onChange={(e) => onChange(e.currentTarget.checked)}
        label={
          <Text size="sm">
            I agree to the{' '}
            <Text component={Link} to={href} size="sm" sx={{ color: 'var(--gs-accent)' }}>
              {linkLabel}
            </Text>
            {text.version > 0 ? ` (version ${text.version})` : ''}
          </Text>
        }
      />
    </Box>
  )
}

/** Current version for API payloads; call after user has loaded consent text. */
export function legalConsentVersion (text: { version: number } | null | undefined): number {
  return text?.version ?? 1
}
